import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import {
  CancelProvisionFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type CancelProvisionFormData,
} from './schema'
import { renderButtonControl } from '../../formRenderFunctions'

export { CancelProvisionFormSchema, type CancelProvisionFormData } from './schema'

export type CancelProvisionComposerProps = {
  deviceNumber: number
  deviceProcurementInfoNumber?: number
  deviceProvisioningInfoNumber?: number
  networkCorridorName?: string
  networkSiteName?: string
  // Cancel (pending request) reverts the device to 'received'. Deprovision (failed
  // attempt) leaves the device in 'testing' with prov 'deprovisioned' so it can then
  // be marked faulty. Defaults to true to preserve the cancel-pending behavior.
  revertToReceived?: boolean
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  showHeader?: boolean
  submitLabel?: string
}

export function CancelProvisionComposer(props: CancelProvisionComposerProps) {
  const {
    deviceNumber,
    deviceProcurementInfoNumber,
    deviceProvisioningInfoNumber,
    networkCorridorName,
    networkSiteName,
    revertToReceived = true,
    onSuccess,
    onError,
    onCancel,
    showHeader = true,
    submitLabel,
  } = props

  // Fetch device data to get network info if not provided
  const { data: deviceDataRaw, isLoading: isDeviceLoading } = useApiQuery('getDeviceByNumber', { deviceNumber })
  const deviceData = deviceDataRaw?.content

  // Get network info from props or device data
  const corridor = networkCorridorName || deviceData?.provisioningInfo?.networkCorridorName || 'Unknown'
  const site = networkSiteName || deviceData?.provisioningInfo?.networkSiteName || 'Unknown'

  // Separate mutations for procurement and provisioning info
  const cancelProcurement = useApiMutation('upsertDeviceProcurementInfo', 'POST', {
    onSuccess: () => {},
    onError: (error) => {
      onError?.(error)
    },
  })

  const cancelProvisioning = useApiMutation('upsertDeviceProvisioningInfo', 'POST', {
    onSuccess: () => {},
    onError: (error) => {
      onError?.(error)
    },
  })

  const upsertDevice = useApiMutation('upsertDevice', 'POST', {
    invalidates: ['getDevices', 'getDeviceByNumber'],
    onSuccess: () => {},
    onError: (error) => {
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: CancelProvisionFormSchema,
    defaultValues: {} as CancelProvisionFormData,
    onSubmit: async () => {
      // Wait for device data to load
      if (isDeviceLoading) return

      const { procurementInfo, provisioningInfo } = transformToApiRequest(
        deviceNumber,
        deviceProcurementInfoNumber,
        deviceProvisioningInfoNumber
      )

      // Merge with existing procurement info
      const existingProcurementInfo = deviceData?.procurementInfo || {}
      const mergedProcurementInfo = {
        ...existingProcurementInfo,
        ...procurementInfo,
      }

      // Merge with existing provisioning info so required fields (corridor/site,
      // provisionedDeviceNumber, etc.) are preserved. The backend defaults any
      // missing numeric field to 0, and getDevice's response schema requires
      // networkCorridorNumber/networkSiteNumber >= 1e9 — so a minimal payload here
      // would wipe them to 0 and make the device fail to load afterwards.
      const existingProvisioningInfo = deviceData?.provisioningInfo || {}
      const mergedProvisioningInfo = {
        ...existingProvisioningInfo,
        ...provisioningInfo,
      }

      // Always set the provisioning record to 'deprovisioned'.
      await cancelProvisioning.mutateAsync({ deviceNumber, provisioningInfo: mergedProvisioningInfo })

      if (revertToReceived) {
        // Cancel a pending request → undo it: revert procurement + device to 'received'.
        await cancelProcurement.mutateAsync({ deviceNumber, procurementInfo: mergedProcurementInfo })
        await upsertDevice.mutateAsync({
          device: {
            deviceNumber,
            deviceStatus: 'received',
            deviceName: deviceData?.device?.deviceName || '',
          },
        })
      }
      // Deprovision (revertToReceived === false) leaves the device in 'testing' with
      // prov 'deprovisioned' — no procurement/device revert — so it can be marked faulty.

      // Success callback
      onSuccess?.()
    },
  })

  const isSubmitting = cancelProcurement.isPending || cancelProvisioning.isPending || upsertDevice.isPending

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{FORM_CONFIG.formTitle}</h2>
            <p className="text-sm text-muted-foreground">
              {FORM_CONFIG.formDescription}
            </p>
          </div>
        )}

        <div className="rounded-md bg-warning/10 border border-warning/20 p-4 space-y-3">
          <p className="text-sm font-medium text-warning">
            {revertToReceived ? 'Cancelling this provisioning request will:' : 'Deprovisioning this device will:'}
          </p>
          <ul className="text-sm text-muted-foreground space-y-2 ml-4">
            {revertToReceived ? (
              <>
                <li>• Revert the device status from <span className="font-semibold">testing</span> to <span className="font-semibold">received</span></li>
                <li>• Revert procurement status from <span className="font-semibold">testing</span> to <span className="font-semibold">received</span></li>
              </>
            ) : (
              <li>• Leave the device in <span className="font-semibold">testing</span> so it can be marked faulty</li>
            )}
            <li>• Set the provisioning request for <span className="font-semibold">{corridor} - {site}</span> to <span className="font-semibold">deprovisioned</span></li>
          </ul>
          <p className="text-sm text-muted-foreground pt-2">
            {revertToReceived
              ? 'You can provision the device again to a different network location after cancelling.'
              : 'You can reprovision the device to a network location, or mark it faulty.'}
          </p>
        </div>

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button'
          })}

          {renderButtonControl({
            label: isSubmitting
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Cancelling...')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Cancel Provision'),
            variant: 'destructive',
            type: 'submit'
          }, { SubmitButton: form.SubmitButton })}
        </div>
      </div>
    </form.AppForm>
  )
}
